import { Box, Stack, Typography, Chip, Divider, Avatar, CircularProgress } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupIcon from "@mui/icons-material/Group";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import VerifiedIcon from "@mui/icons-material/Verified";
import PersonIcon from "@mui/icons-material/Person";
import LayersIcon from "@mui/icons-material/Layers";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { useState, useEffect, useMemo, useCallback } from "react";

const LEVEL_CONFIG = {
	L1: { label: "Level 1", rate: 0.5, color: "#006064", bg: "#E0F7FA", accent: "#00ACC1" },
	L2: { label: "Level 2", rate: 0.3, color: "#1565C0", bg: "#E3F2FD", accent: "#1976D2" },
	L3: { label: "Level 3", rate: 0.2, color: "#4527A0", bg: "#EDE7F6", accent: "#7E57C2" },
};

const QUALIFY = { personal: 25, network: 100 };

const PERFORMANCE_CONFIG = {
	Strong: { icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, color: "#2E7D32", bg: "rgba(46,125,50,0.12)", label: "Strong" },
	Declining: { icon: <TrendingDownIcon sx={{ fontSize: 16 }} />, color: "#C62828", bg: "rgba(198,40,40,0.1)", label: "Declining" },
	Inactive: { icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />, color: "#78909C", bg: "rgba(120,144,156,0.12)", label: "Inactive" },
};

// ─── Build tree from real API network object ──────────────────────────────────
// Network shape:
//   { agent_id, agent_name, l1: [...], l2: [...], l3: [], ... }
// Each member has: ID, name, source_agent, override_earning, active_members,
//   network_members, qualification_status, license_status, performance_indicator,
//   leadershipBonus, own_payout_status

const buildTreeFromNetwork = (network) => {
	if (!network || !network.l1) return [];

	const l1Members = network.l1 ?? [];
	const l2Members = network.l2 ?? [];
	const l3Members = network.l3 ?? [];

	// Index L2 and L3 by source_agent for fast lookup
	const l2BySource = {};
	l2Members.forEach((m) => {
		const key = m.source_agent;
		if (!l2BySource[key]) l2BySource[key] = [];
		l2BySource[key].push(m);
	});

	const l3BySource = {};
	l3Members.forEach((m) => {
		const key = m.source_agent;
		if (!l3BySource[key]) l3BySource[key] = [];
		l3BySource[key].push(m);
	});

	const makeNode = (member, levelKey) => ({
		id: member.ID,
		name: member.name,
		qualification_status: member.qualification_status,
		override_earning: member.override_earning ?? 0,
		own_override_earning: member.own_override_earning ?? 0,
		license_status: member.license_status,
		leadership_bonus: member.leadershipBonus ?? [],
		// leadership_bonus_progress uses network_members as the running total
		leadership_bonus_progress: member.network_members ?? 0,
		performance: member.performance_indicator,
		level: levelKey,
		activeMembers: member.active_members ?? 0,
		networkMembers: member.network_members ?? 0,
		own_payout_status: member.own_payout_status,
		// Deactivated agents stay in the tree but contribute nothing upline.
		optedIn: Number(member.is_activated ?? 1) === 1,
		// An opted-out agent the backend kept only to hold their downlines onto the
		// tree. It carries no identity, so it renders greyed and cannot be selected.
		isPlaceholder: member.is_placeholder === true,
		downlines: [],
	});

	return l1Members.map((l1Member) => {
		const l1Node = makeNode(l1Member, "L1");

		const l2Children = l2BySource[l1Member.ID] ?? [];
		l1Node.downlines = l2Children.map((l2Member) => {
			const l2Node = makeNode(l2Member, "L2");

			const l3Children = l3BySource[l2Member.ID] ?? [];
			l2Node.downlines = l3Children.map((l3Member) => makeNode(l3Member, "L3"));

			return l2Node;
		});

		return l1Node;
	});
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getOverrideContribution = (agent) => agent.override_earning ?? 0;
const getOwnOverrideEarning = (agent) => agent.own_override_earning ?? 0;

const getQualification = (agent) => {
	const personalMet = agent.activeMembers >= QUALIFY.personal;
	const networkTotal = agent.networkMembers;
	const networkMet = networkTotal >= QUALIFY.network;
	const qualification_badge_status = (agent.qualification_status || "").toLowerCase();
	return { personalMet, networkMet, networkTotal, qualification_badge_status, qualified: personalMet || networkMet };
};

const getInitials = (name) =>
	name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

// ─── Tree connector wrappers ──────────────────────────────────────────────────

const TreeChildren = ({ children }) => (
	<Box
		sx={{
			position: "relative",
			ml: "18px",
			pl: "16px",
			"&::before": {
				content: '""',
				position: "absolute",
				left: 0,
				top: 0,
				bottom: "20px",
				width: "2px",
				background: "linear-gradient(to bottom, #80DEEA, #E0E0E0)",
				borderRadius: 2,
			},
		}}
	>
		{children}
	</Box>
);

const TreeRowWrap = ({ children, isRoot }) =>
	isRoot ? (
		<Box sx={{ mb: "6px" }}>{children}</Box>
	) : (
		<Box
			sx={{
				position: "relative",
				mb: "6px",
				"&::before": {
					content: '""',
					position: "absolute",
					left: "-16px",
					top: "50%",
					width: "14px",
					height: "2px",
					backgroundColor: "#80DEEA",
				},
			}}
		>
			{children}
		</Box>
	);

const getInitialExpanded = (nodeLevel, levelFilter) => {
	if (!levelFilter) return true;
	switch (levelFilter) {
		case "l1":
			return false;
		case "l2":
			return nodeLevel === "L1";
		case "l3":
			return true;
		default:
			return true;
	}
};

// ─── Single agent node card ───────────────────────────────────────────────────

const AgentNode = ({ agent, isRoot = false, selectedId, onSelect, levelFilter, expandAll }) => {
	const [expanded, setExpanded] = useState(() => getInitialExpanded(agent.level, levelFilter));
	const config = LEVEL_CONFIG[agent.level];
	const hasKids = agent.downlines?.length > 0;
	const override = getOverrideContribution(agent);
	const { qualification_badge_status } = getQualification(agent);
	// A placeholder has no agent behind it to open a detail panel for, so it is
	// never selectable. Expanding still works — that is what keeps the opted-in
	// downlines underneath reachable, which is the whole point of keeping the node.
	const isPlaceholder = agent.isPlaceholder === true;
	const isSelected = !isPlaceholder && selectedId === agent.id;

	useEffect(() => {
		if (expandAll === null) return;
		setExpanded(expandAll);
	}, [expandAll]);

	const handleClick = () => {
		if (!isPlaceholder) onSelect(agent);
		if (hasKids) setExpanded((p) => !p);
	};

	return (
		<Box sx={{ width: "100%" }}>
			<TreeRowWrap isRoot={isRoot}>
				<Box
					onClick={handleClick}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						px: 1.5,
						py: 1,
						borderRadius: "10px",
						border: isSelected ? `2px solid ${config.accent}` : "1.5px solid #E8EDF2",
						backgroundColor: isSelected ? config.bg : isPlaceholder ? "#F2F4F6" : "#FAFBFC",
						// Placeholders still take a click to expand, but nothing opens.
						cursor: isPlaceholder && !hasKids ? "default" : "pointer",
						userSelect: "none",
						opacity: isPlaceholder ? 0.65 : 1,
						boxShadow: isSelected ? `0 0 0 3px ${config.bg}` : "none",
						transition: "all 0.15s ease",
						"&:hover": isPlaceholder ? {} : { backgroundColor: config.bg, borderColor: config.accent },
					}}
				>
					<Avatar
						sx={{
							width: 34,
							height: 34,
							fontSize: 12,
							fontWeight: 700,
							bgcolor: isPlaceholder ? "#B0BEC5" : config.accent,
							flexShrink: 0,
						}}
					>
						{isPlaceholder ? <PersonOffIcon sx={{ fontSize: 16 }} /> : getInitials(agent.name)}
					</Avatar>
					<Box flex={1} minWidth={0}>
						<Stack direction="row" alignItems="center" spacing={0.75}>
							<Typography
								fontSize={13}
								fontWeight={700}
								color={isPlaceholder ? "text.disabled" : "text.primary"}
								noWrap
								sx={{ maxWidth: 220, fontStyle: isPlaceholder ? "italic" : "normal" }}
							>
								{agent.name}
							</Typography>
							{/* A placeholder has no eligibility, earnings or member count to report. */}
							{!isPlaceholder &&
								(qualification_badge_status === "passed" ? (
									<VerifiedIcon sx={{ fontSize: 13, color: "#2E7D32" }} />
								) : qualification_badge_status === "in_progress" ? (
									<HourglassTopIcon sx={{ fontSize: 13, color: "#EDB100" }} />
								) : (
									<CancelIcon sx={{ fontSize: 13, color: "#D32F2F" }} />
								))}
						</Stack>
						<Stack direction="row" alignItems="center" spacing={0.75} mt={0.25}>
							<Chip
								label={agent.level}
								size="small"
								sx={{
									height: 16,
									fontSize: 10,
									fontWeight: 700,
									bgcolor: isPlaceholder ? "#ECEFF1" : config.bg,
									color: isPlaceholder ? "text.disabled" : config.color,
									"& .MuiChip-label": { px: 0.75 },
								}}
							/>
							{isPlaceholder ? (
								<Typography fontSize={11} color="text.disabled" fontStyle="italic">
									excluded from the portal
								</Typography>
							) : (
								<Typography fontSize={11} color="text.secondary">
									{agent.activeMembers} members
								</Typography>
							)}
						</Stack>
					</Box>
					<Stack alignItems="flex-end" flexShrink={0} spacing={0.25}>
						{!isPlaceholder && (
							<Typography fontSize={12} fontWeight={700} color={config.accent}>
								${override.toFixed(2)}
							</Typography>
						)}
						{hasKids && (
							<ChevronRightIcon
								sx={{
									fontSize: 15,
									color: "text.disabled",
									transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
									transition: "transform 0.18s",
								}}
							/>
						)}
					</Stack>
				</Box>
			</TreeRowWrap>

			{hasKids && expanded && (
				<TreeChildren>
					{agent.downlines.map((child) => (
						<AgentNode
							key={child.id}
							agent={child}
							isRoot={false}
							selectedId={selectedId}
							onSelect={onSelect}
							levelFilter={levelFilter}
							expandAll={expandAll}
						/>
					))}
				</TreeChildren>
			)}
		</Box>
	);
};

// ─── Detail panel helpers ─────────────────────────────────────────────────────

const DetailRow = ({ icon, label, value, valueColor }) => (
	<Stack direction="row" alignItems="center" justifyContent="space-between" py={1.25} spacing={2}>
		<Stack direction="row" alignItems="center" spacing={1}>
			<Box sx={{ color: "#90A4AE", display: "flex" }}>{icon}</Box>
			<Typography fontSize={13} color="text.secondary">
				{label}
			</Typography>
		</Stack>
		<Typography fontSize={13} fontWeight={700} color={valueColor || "text.primary"}>
			{value}
		</Typography>
	</Stack>
);

const QualBar = ({ label, value, max, met, color }) => (
	<Box width="100%">
		<Stack direction="row" justifyContent="space-between" mb={0.5} spacing={2}>
			<Typography fontSize={11} color="text.secondary">
				{label}
			</Typography>
			<Stack direction="row" alignItems="center" spacing={0.5}>
				<Typography fontSize={11} fontWeight={700} color={met ? "#2E7D32" : "text.secondary"}>
					{value} / {max}
				</Typography>
				{met && <VerifiedIcon sx={{ fontSize: 12, color: "#2E7D32" }} />}
			</Stack>
		</Stack>
		<Box sx={{ height: 5, borderRadius: 3, bgcolor: "#F0F0F0", overflow: "hidden" }}>
			<Box
				sx={{
					height: "100%",
					width: `${Math.min(100, (value / max) * 100)}%`,
					bgcolor: met ? "#2E7D32" : color,
					borderRadius: 3,
					transition: "width 0.4s ease",
				}}
			/>
		</Box>
	</Box>
);

const PerformanceBadge = ({ status }) => {
	const cfg = PERFORMANCE_CONFIG[status] ?? PERFORMANCE_CONFIG.Inactive;
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				px: 1,
				py: 0.4,
				borderRadius: "20px",
				bgcolor: "#fff",
				border: `1px solid ${cfg.color}22`,
			}}
		>
			<Box sx={{ color: cfg.color, display: "flex", alignItems: "center" }}>{cfg.icon}</Box>
			<Typography fontSize={11} fontWeight={700} color={cfg.color}>
				{cfg.label}
			</Typography>
		</Box>
	);
};

const LeadershipBonusTiers = ({ agent, config }) => {
	const [open, setOpen] = useState(true);
	// Use network_members as the progress baseline (matches API field)
	const progress = agent.leadership_bonus_progress ?? 0;

	return (
		<Box>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				py={1.25}
				onClick={() => setOpen((p) => !p)}
				sx={{ cursor: "pointer", "&:hover": { opacity: 0.75 } }}
			>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Box sx={{ color: "#90A4AE", display: "flex" }}>
						<MonetizationOnIcon sx={{ fontSize: 16 }} />
					</Box>
					<Typography fontSize={13} color="text.secondary">
						Leadership Bonus Tiers
					</Typography>
				</Stack>
				<ChevronRightIcon
					sx={{ fontSize: 15, color: "text.disabled", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.18s" }}
				/>
			</Stack>

			{open && (
				<Stack spacing={1.5} py={1.5} px={1.5} gap={0.5}>
					{agent.leadership_bonus.map((tier, i) => {
						const tierProgress = Number(tier.progress ?? progress);
						const target = Number(tier.target);
						const pct = Math.min(100, (tierProgress / target) * 100);
						const achieved = Number(tier.achieved) === 1;

						return (
							<Box key={tier.id ?? i} width="100%">
								<Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
									<Stack direction="row" alignItems="center" spacing={0.75}>
										{achieved ? (
											<VerifiedIcon sx={{ fontSize: 12, color: "#2E7D32" }} />
										) : (
											<HourglassTopIcon sx={{ fontSize: 12, color: "#EDB100" }} />
										)}
										<Typography fontSize={12} color="text.secondary">
											Tier {i + 1}
										</Typography>
										<Chip
											label={
												tier.status === "approved"
													? "Approved"
													: tier.status === "rejected"
														? "Rejected"
														: achieved
															? "Achieved"
															: "In Progress"
											}
											size="small"
											sx={{
												height: 16,
												fontSize: 10,
												fontWeight: 700,
												bgcolor: tier.status === "approved" ? "#E8F5E9" : tier.status === "rejected" ? "#FFEBEE" : "#FFF8E1",
												color: tier.status === "approved" ? "#2E7D32" : tier.status === "rejected" ? "#C62828" : "#EDB100",
												"& .MuiChip-label": { px: 0.75 },
											}}
										/>
									</Stack>
									<Typography fontSize={12} fontWeight={700} color={config.accent}>
										+${Number(tier.reward).toLocaleString()}
									</Typography>
								</Stack>
								<Box sx={{ height: 5, borderRadius: 3, bgcolor: "#F0F0F0", overflow: "hidden" }}>
									<Box
										sx={{
											height: "100%",
											width: `${pct}%`,
											bgcolor: tier.status === "approved" ? "#4CAF50" : tier.status === "rejected" ? "#EF5350" : config.accent,
											borderRadius: 3,
											transition: "width 0.4s ease",
										}}
									/>
								</Box>
								<Stack direction="row" justifyContent="space-between" mt={0.5}>
									<Typography fontSize={10} color={achieved ? "#2E7D32" : "text.disabled"} fontWeight={achieved ? 700 : 400}>
										{Math.min(tierProgress, target).toLocaleString()} / {target.toLocaleString()}
									</Typography>
								</Stack>
							</Box>
						);
					})}
				</Stack>
			)}
		</Box>
	);
};

const NodeDetailPanel = ({ agent }) => {
	if (!agent) {
		return (
			<Stack
				flex={1}
				alignItems="center !important"
				justifyContent="center"
				spacing={1.5}
				sx={{ border: "1.5px dashed #E0E0E0", borderRadius: 3, p: 4, background: "#FAFBFC", minHeight: 300 }}
			>
				<AccountTreeIcon sx={{ fontSize: 40, color: "#CFD8DC" }} />
				<Typography fontSize={14} color="text.disabled" textAlign="center">
					Click any node to view
					<br />
					agent details
				</Typography>
			</Stack>
		);
	}

	const config = LEVEL_CONFIG[agent.level];
	const override = getOwnOverrideEarning(agent);

	const { qualified, personalMet, networkMet, networkTotal, qualification_badge_status } = getQualification(agent);

	return (
		<Box
			flex={1}
			sx={{
				border: `1.5px solid ${config.accent}`,
				borderRadius: 3,
				overflow: "hidden",
				background: "#fff",
				boxShadow: `0 4px 20px ${config.bg}`,
			}}
		>
			{/* Header */}
			<Box sx={{ background: `linear-gradient(135deg, ${config.accent} 0%, ${config.color} 100%)`, px: 3, py: 2.5 }}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Avatar
						sx={{
							width: 52,
							height: 52,
							fontSize: 18,
							fontWeight: 800,
							bgcolor: "rgba(255,255,255,0.25)",
							color: "#fff",
							border: "2px solid rgba(255,255,255,0.5)",
						}}
					>
						{getInitials(agent.name)}
					</Avatar>
					<Stack direction="row" alignItems="center" justifyContent="space-between" flex={1}>
						<Box>
							<Typography fontSize={17} fontWeight={800} color="#fff">
								{agent.name}
							</Typography>
							<Stack direction="row" alignItems="center" spacing={0.75} mt={0.5}>
								<Chip
									label={config.label}
									size="small"
									sx={{
										height: 18,
										fontSize: 10,
										fontWeight: 700,
										bgcolor: "rgba(255,255,255,0.25)",
										color: "#fff",
										"& .MuiChip-label": { px: 1 },
									}}
								/>
								{/* An opted-out agent has no eligibility to report — say why instead. */}
								{agent.optedIn ? (
									<Chip
										icon={
											qualification_badge_status === "passed" ? (
												<VerifiedIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />
											) : qualification_badge_status === "in_progress" ? (
												<HourglassTopIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />
											) : (
												<CancelIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />
											)
										}
										label={
											qualification_badge_status === "passed"
												? "Override Eligible"
												: qualification_badge_status === "in_progress"
													? "Qualification In Progress"
													: "Not Yet Eligible"
										}
										size="small"
										sx={{
											height: 18,
											fontSize: 10,
											fontWeight: 700,
											bgcolor:
												qualification_badge_status === "passed"
													? "#2E7D32"
													: qualification_badge_status === "in_progress"
														? "#EDB100"
														: "#D32F2F",
											color: "#fff",
											"& .MuiChip-label": { px: 0.75 },
										}}
									/>
								) : (
									<Chip
										icon={<CancelIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />}
										label="Opted Out of Service"
										size="small"
										sx={{
											height: 18,
											fontSize: 10,
											fontWeight: 700,
											bgcolor: "#C62828",
											color: "#fff",
											"& .MuiChip-label": { px: 0.75 },
										}}
									/>
								)}
							</Stack>
						</Box>
						{agent.performance && <PerformanceBadge status={agent.performance} />}
					</Stack>
				</Stack>
			</Box>

			{/* Details */}
			<Box px={3} py={1}>
				<DetailRow
					icon={<LayersIcon sx={{ fontSize: 16 }} />}
					label="Level"
					value={`${agent.level} — ${config.label}`}
					valueColor={config.color}
				/>
				<Divider />
				<DetailRow icon={<PersonIcon sx={{ fontSize: 16 }} />} label="Personal Members" value={agent.activeMembers.toLocaleString()} />
				<Divider />
				<DetailRow icon={<GroupIcon sx={{ fontSize: 16 }} />} label="Network Members" value={networkTotal.toLocaleString()} />
				<Divider />
				<DetailRow
					icon={<MonetizationOnIcon sx={{ fontSize: 16 }} />}
					label="Own Override Earning"
					value={`$${override.toFixed(2)}`}
					valueColor={config.accent}
				/>
				<Divider />
				<DetailRow
					icon={
						<Box sx={{ fontSize: 16, display: "flex" }}>
							<Typography fontSize={12} fontWeight={700} color={agent.own_payout_status === "Approved" ? "#2E7D32" : "#E65100"}>
								$
							</Typography>
						</Box>
					}
					label="Own Payout Status"
					value={agent.own_payout_status ?? "—"}
					valueColor={agent.own_payout_status === "Approved" ? "#2E7D32" : "#E65100"}
				/>
				<Divider />
				{agent.leadership_bonus?.length > 0 && <LeadershipBonusTiers agent={agent} config={config} />}
				<Divider />
				<DetailRow
					icon={<CreditCardIcon sx={{ fontSize: 16 }} />}
					label="License Status"
					value={agent.license_status}
					valueColor={agent.license_status === "Active" ? "#2E7D32" : "#C62828"}
				/>
				<Divider />
				<DetailRow
					icon={
						qualification_badge_status === "passed" ? (
							<VerifiedIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
						) : qualification_badge_status === "in_progress" ? (
							<HourglassTopIcon sx={{ fontSize: 16, color: "#EDB100" }} />
						) : (
							<CancelIcon sx={{ fontSize: 16, color: "#C62828" }} />
						)
					}
					label="Qualification Status"
					value={
						qualification_badge_status === "passed"
							? "Override Eligible"
							: qualification_badge_status === "in_progress"
								? "Qualification In Progress"
								: "Not Yet Eligible"
					}
					valueColor={
						qualification_badge_status === "passed" ? "#2E7D32" : qualification_badge_status === "in_progress" ? "#EDB100" : "#C62828"
					}
				/>
			</Box>

			{/* Qual bars */}
			<Box px={3} pb={2} pt={0.5}>
				<Typography fontSize={11} fontWeight={600} color="text.secondary" mb={1.25}>
					QUALIFICATION REQUIREMENTS{" "}
					<Typography component="span" fontSize={10} color="text.disabled">
						(either condition qualifies)
					</Typography>
				</Typography>
				<Stack spacing={1.25}>
					<QualBar
						label="Personal Active Members"
						value={agent.activeMembers}
						max={QUALIFY.personal}
						met={personalMet}
						color={config.accent}
					/>
					<QualBar label="Total Network Members" value={networkTotal} max={QUALIFY.network} met={networkMet} color={config.accent} />
				</Stack>
				{!qualified && (
					<Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "#FFF3E0", border: "1px solid #FFE0B2" }}>
						<Typography fontSize={11} color="#E65100">
							Needs <strong>{Math.max(0, QUALIFY.personal - agent.activeMembers)}</strong> more personal members or{" "}
							<strong>{Math.max(0, QUALIFY.network - networkTotal)}</strong> more network members to qualify.
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
};

// ─── Root component ───────────────────────────────────────────────────────────

export default function HierarchyRatesOverride({ currentAgent, levelFilter, network, loading }) {
	const [selectedAgent, setSelectedAgent] = useState(null);
	const [expandAll, setExpandAll] = useState(null);

	// Build tree from real network data
	const tree = useMemo(() => buildTreeFromNetwork(network), [network]);

	useEffect(() => {
		setSelectedAgent(null);
		setExpandAll(null);
	}, [network]);

	const handleExpandCollapse = useCallback(() => {
		setExpandAll((prev) => (prev === false ? true : false));
	}, []);

	const isCollapsed = expandAll === false;

	if (loading) {
		return (
			<Stack alignItems="center !important" justifyContent="center" py={8}>
				<CircularProgress size={32} sx={{ color: "#1D54A2" }} />
				<Typography fontSize={13} color="text.secondary" mt={2}>
					Loading network...
				</Typography>
			</Stack>
		);
	}

	return (
		<Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} alignItems="flex-start">
			{/* LEFT — Tree */}
			<Box
				sx={{
					flex: "0 0 auto",
					width: { xs: "100%", lg: 520 },
					border: "1.5px solid #E8EDF2",
					borderRadius: 3,
					p: 2.5,
					background: "#fff",
					boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1.25} mb={2}>
					<Box
						sx={{
							width: 34,
							height: 34,
							borderRadius: 2,
							bgcolor: "#E0F7FA",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<AccountTreeIcon sx={{ fontSize: 18, color: "#00ACC1" }} />
					</Box>
					<Box>
						<Typography fontWeight={700} fontSize={15} color="text.primary">
							Agent Network
						</Typography>
						<Typography fontSize={11} color="text.secondary">
							{tree.length} direct agent{tree.length !== 1 ? "s" : ""}
						</Typography>
					</Box>
				</Stack>

				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
					<Stack direction="row" spacing={1} flexWrap="wrap">
						{Object.entries(LEVEL_CONFIG).map(([key, cfg]) => (
							<Stack key={key} direction="row" alignItems="center" spacing={0.5}>
								<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.accent }} />
								<Typography fontSize={10} color="text.secondary">
									{key}
								</Typography>
							</Stack>
						))}
					</Stack>
					<Typography
						fontSize={11}
						fontWeight={600}
						color="primary.main"
						onClick={handleExpandCollapse}
						sx={{ cursor: "pointer", userSelect: "none", "&:hover": { opacity: 0.7 }, flexShrink: 0 }}
					>
						{isCollapsed ? "Expand All" : "Collapse All"}
					</Typography>
				</Stack>

				<Divider sx={{ mb: 2 }} />

				{tree.length === 0 ? (
					<Stack alignItems="center !important" py={4} spacing={1}>
						<AccountTreeIcon sx={{ fontSize: 32, color: "#CFD8DC" }} />
						<Typography fontSize={13} color="text.disabled">
							No downlines for this agent.
						</Typography>
					</Stack>
				) : (
					<Stack spacing={0.5}>
						{tree.map((agent) => (
							<AgentNode
								key={agent.id}
								agent={agent}
								isRoot
								selectedId={selectedAgent?.id}
								onSelect={setSelectedAgent}
								levelFilter={levelFilter}
								expandAll={expandAll}
							/>
						))}
					</Stack>
				)}
			</Box>

			{/* RIGHT — Detail */}
			<Box flex={1} sx={{ width: "100%" }}>
				<NodeDetailPanel agent={selectedAgent} />
			</Box>
		</Stack>
	);
}
